import { useFavoriteTab } from '../../../hooks/useFavoriteTab';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { PencilIcon, TrashIcon, PaperAirplaneIcon } from '@heroicons/react/24/outline';
import { TanTable } from '../../../components/TanTable';
import Notification, { NotificationContainer } from '../../../components/Notification';
import remindersService from '../../../services/compliance/reminders.service';
import { AlertType, ReminderSchedule } from '../../../types/compliance';
import ReminderScheduleDrawer from './ReminderScheduleDrawer';
import ReminderLogTab from './ReminderLogTab';

const ALERT_TYPE_LABELS: Record<AlertType, string> = {
  contract_renewal: 'Contract Renewal',
  contract_renewal_action: 'Contract Renewal Action',
  contract_notice_period: 'Contract Notice Period',
  tprm_review: 'TPRM Review',
  remediation_due: 'Remediation Due',
  risk_acceptance_expiry: 'Risk Acceptance Expiry',
  evidence_expiry: 'Evidence Expiry',
};

const ALERT_TYPE_BADGE: Record<AlertType, string> = {
  contract_renewal: 'bg-blue-200 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  contract_renewal_action: 'bg-indigo-200 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200',
  contract_notice_period: 'bg-purple-200 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
  tprm_review: 'bg-teal-200 text-teal-800 dark:bg-teal-900 dark:text-teal-200',
  remediation_due: 'bg-amber-200 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
  risk_acceptance_expiry: 'bg-orange-200 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
  evidence_expiry: 'bg-pink-200 text-pink-800 dark:bg-pink-900 dark:text-pink-200',
};

export default function ReminderSchedules() {
  const [tab, setTab] = useState<'schedules' | 'log'>('schedules');
  useFavoriteTab(tab, setTab, ['schedules', 'log']);
  const [data, setData] = useState<ReminderSchedule[]>([]);
  const [loading, setLoading] = useState(true);
  const [dispatching, setDispatching] = useState(false);
  const [errorNotification, setErrorNotification] =
    useState<{ title: string; message: string } | null>(null);
  const [successNotification, setSuccessNotification] =
    useState<{ title: string; message: string } | null>(null);
  const [drawer, setDrawer] = useState<{
    open: boolean;
    schedule: ReminderSchedule | null; // null = create mode
  }>({ open: false, schedule: null });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await remindersService.listSchedules();
      setData(rows);
    } catch (error: any) {
      const msg = error?.response?.data?.detail || error?.message || 'Unknown error occurred';
      setErrorNotification({ title: 'Failed to load schedules', message: msg });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleDelete = useCallback(
    async (row: ReminderSchedule) => {
      if (!window.confirm(`Are you sure you want to delete schedule "${row.label}"?`)) return;
      try {
        await remindersService.deleteSchedule(row.id);
        setSuccessNotification({
          title: 'Deleted Successfully',
          message: `Schedule "${row.label}" has been deleted.`,
        });
        loadData();
      } catch (error: any) {
        const msg = error?.response?.data?.detail || error?.message || 'Unknown error occurred';
        setErrorNotification({ title: 'Failed to Delete', message: msg });
      }
    },
    [loadData],
  );

  const handleDispatchNow = useCallback(async () => {
    setDispatching(true);
    try {
      const result = await remindersService.dispatchNow();
      setSuccessNotification({
        title: 'Dispatch Complete',
        message: `Records: ${result.records}, Inserted: ${result.inserted}, Sent: ${result.sent}, Failed: ${result.failed}, Skipped: ${result.skipped}, Contracts expired: ${result.expired ?? 0}`,
      });
    } catch (error: any) {
      const msg = error?.response?.data?.detail || error?.message || 'Unknown error occurred';
      setErrorNotification({ title: 'Dispatch Failed', message: msg });
    } finally {
      setDispatching(false);
    }
  }, []);

  const columns = useMemo<ColumnDef<ReminderSchedule>[]>(
    () => [
      {
        accessorKey: 'alert_type',
        header: () => <span className="pl-3">Alert Type</span>,
        enableSorting: true,
        size: 220,
        cell: ({ row }) => {
          const t = row.original.alert_type;
          return (
            <span className="pl-3">
              <span
                className={`px-2 py-0.5 text-xs font-medium rounded ${ALERT_TYPE_BADGE[t] || ''}`}
              >
                {ALERT_TYPE_LABELS[t] || t}
              </span>
            </span>
          );
        },
      },
      {
        accessorKey: 'days_before',
        header: 'Days Before',
        enableSorting: true,
        size: 120,
        cell: ({ row }) => <span>{row.original.days_before}</span>,
      },
      {
        accessorKey: 'label',
        header: 'Label',
        enableSorting: true,
        size: 240,
        cell: ({ row }) => <span>{row.original.label}</span>,
      },
      {
        accessorKey: 'is_active',
        header: 'Active',
        enableSorting: true,
        size: 100,
        cell: ({ row }) => {
          const active = row.original.is_active;
          return (
            <span
              className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded ${
                active
                  ? 'bg-green-200 text-green-800 dark:bg-green-900 dark:text-green-200'
                  : 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
              }`}
            >
              <span
                className={`w-2 h-2 rounded-full ${active ? 'bg-green-600' : 'bg-gray-500'}`}
              />
              {active ? 'Active' : 'Inactive'}
            </span>
          );
        },
      },
      {
        id: 'actions',
        header: 'Actions',
        size: 100,
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }) => (
          <div className="flex gap-2 justify-end">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setDrawer({ open: true, schedule: row.original });
              }}
              className="p-1 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
              title="Edit"
            >
              <PencilIcon className="w-4 h-4" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(row.original);
              }}
              className="p-1 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 transition-colors"
              title="Delete"
            >
              <TrashIcon className="w-4 h-4" />
            </button>
          </div>
        ),
      },
    ],
    [handleDelete],
  );

  return (
    <div className="h-full w-full p-6 pb-24">
      <div className="mb-4">
        <h1 className="text-2xl font-bold">Reminders</h1>
        <p className="text-sm text-muted">
          Configure reminder schedules and review dispatch history.
        </p>
      </div>

      {/* Tab bar */}
      <div className="flex gap-2 border-b border-gray-200 dark:border-gray-700 mb-4">
        <button
          type="button"
          onClick={() => setTab('schedules')}
          className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${
            tab === 'schedules'
              ? 'border-blue-600 text-blue-600 dark:text-blue-400'
              : 'border-transparent text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
          }`}
        >
          Schedules
        </button>
        <button
          type="button"
          onClick={() => setTab('log')}
          className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${
            tab === 'log'
              ? 'border-blue-600 text-blue-600 dark:text-blue-400'
              : 'border-transparent text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
          }`}
        >
          Log
        </button>
      </div>

      {tab === 'schedules' ? (
        <>
          <div className="flex justify-end mb-3">
            <button
              type="button"
              onClick={handleDispatchNow}
              disabled={dispatching}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-accent-fg bg-accent rounded-lg hover:bg-accent-hover disabled:opacity-50"
              title="Run the daily dispatcher now"
            >
              {dispatching ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Dispatching...
                </>
              ) : (
                <>
                  <PaperAirplaneIcon className="w-4 h-4" />
                  Dispatch Now
                </>
              )}
            </button>
          </div>

          <TanTable
            data={data}
            columns={columns}
            loading={loading}
            settingsKey="compliance-reminder-schedules-table"
            fieldName="compliance-reminder-schedules-table"
            searchPlaceholder="Search schedules..."
            onRowClick={(row) => setDrawer({ open: true, schedule: row })}
            enableGrouping={false}
            enableColumnFilters={true}
            cardTitle="Reminder Schedules"
            lockedColumns={[{ id: 'actions', side: 'right' }]}
            showEditFiltersButton={true}
            editFiltersButtonIcon="plus"
            editFiltersButtonTooltip="New Schedule"
            onEditFilters={() => setDrawer({ open: true, schedule: null })}
          />

          <ReminderScheduleDrawer
            open={drawer.open}
            schedule={drawer.schedule}
            onClose={() => setDrawer((d) => ({ ...d, open: false }))}
            onSaved={() => {
              loadData();
            }}
            onDeleted={() => {
              setDrawer({ open: false, schedule: null });
              loadData();
            }}
          />
        </>
      ) : (
        <ReminderLogTab />
      )}

      {errorNotification && (
        <NotificationContainer position="top-right">
          <Notification
            type="error"
            title={errorNotification.title}
            message={errorNotification.message}
            showClose={true}
            onClose={() => setErrorNotification(null)}
            autoDismiss={5000}
          />
        </NotificationContainer>
      )}

      {successNotification && (
        <NotificationContainer position="top-right">
          <Notification
            type="success"
            title={successNotification.title}
            message={successNotification.message}
            showClose={true}
            onClose={() => setSuccessNotification(null)}
            autoDismiss={5000}
          />
        </NotificationContainer>
      )}
    </div>
  );
}
